import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-file-upload',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule
  ],
  templateUrl: './file-upload.component.html',
  styleUrls: ['./file-upload.component.scss']
})
export class FileUploadComponent {
  @Output() fileSelected = new EventEmitter<File[]>();

  @Input() maxFiles = 5; // Default max files allowed
  @Input() allowMultiple = false; // Allow multiple files
  @Input() acceptedTypes = ''; // Accepted file types (e.g., "image/*, .pdf")

  selectedFiles: File[] = [];
  previewUrls: string[] = [];

  // Non-cryptographic use: generating unique DOM element ID for file input
  // NOSONAR: Math.random() is safe for non-cryptographic purposes (DOM element ID generation)
  uniqueId = `fileInput-${Math.random().toString(36).substring(2, 11)}`;

  // Handles file selection from input
  onFileSelected(event: Event) {
    const target = event.target as HTMLInputElement;
    if (target.files && target.files.length > 0) {
      this.handleFiles(Array.from(target.files));

      // Reset the file input to allow re-selection of the same file
      target.value = '';
    }
  }

  // Handles file drop in the drop zone
  onFileDropped(event: DragEvent) {
    event.preventDefault();
    if (event.dataTransfer?.files.length) {
      this.handleFiles(Array.from(event.dataTransfer.files));
    }
  }

  // Prevent default behavior when dragging over the drop zone
  onDragOver(event: DragEvent) {
    event.preventDefault();
  }

  // Process and validate the files
  private handleFiles(files: File[]) {
    for (let file of files) {
      if (this.acceptedTypes && !new RegExp(this.acceptedTypes.replace(',', '|')).exec(file.type)) {
        alert(`File type not allowed: ${file.name}`);
        continue;
      }

      if (this.allowMultiple) {
        if (this.selectedFiles.length >= this.maxFiles) {
          alert(`You can only upload up to ${this.maxFiles} files.`);
          return;
        }
        this.selectedFiles.push(file);
      } else {
        // Replace the existing file if allowMultiple is false
        this.selectedFiles = [file];
        this.previewUrls = [];
      }

      this.fileSelected.emit([...this.selectedFiles]);

      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (!this.allowMultiple) {
            this.previewUrls = []; // Clear existing preview for single file upload
          }
          this.previewUrls.push(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      }
    }
  }

  // Remove a single file
  removeFile(file: File) {
    const index = this.selectedFiles.indexOf(file);
    if (index > -1) {
      this.selectedFiles.splice(index, 1);
      this.previewUrls.splice(index, 1);
      this.fileSelected.emit([...this.selectedFiles])
    }
  }

  // Clear all selected files
  clearFiles() {
    this.selectedFiles = [];
    this.previewUrls = [];
    this.fileSelected.emit(this.selectedFiles)
  }
}
